/**
 * The staff "Forgot password?" link. Supabase Auth emails a link that verifies the
 * one-time token and redirects to `/login#access_token=…&refresh_token=…&type=recovery`
 * (or `#error=…&error_code=otp_expired…` when the link is used up or too old).
 * The free trial's email (request-demo, `type=invite`) comes back the same way: the
 * person is signed in and chooses their first password.
 *
 * The CRM's client has `detectSessionInUrl: false` (invite tokens also travel in a
 * fragment, for another page), so the sign-in page reads the fragment itself with this.
 */
export type RecoveryFragment =
  | { kind: 'none' }
  | { kind: 'session'; purpose: 'recovery' | 'invite'; accessToken: string; refreshToken: string }
  | { kind: 'error'; code: string | null; description: string | null };

export function parseRecoveryFragment(hash: string): RecoveryFragment {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw === '') return { kind: 'none' };
  const params = new URLSearchParams(raw);

  const error = params.get('error') ?? params.get('error_code');
  if (error) {
    return {
      kind: 'error',
      code: params.get('error_code') ?? params.get('error'),
      description: params.get('error_description'),
    };
  }

  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const type = params.get('type');
  if ((type === 'recovery' || type === 'invite') && accessToken && refreshToken) {
    return { kind: 'session', purpose: type, accessToken, refreshToken };
  }
  return { kind: 'none' };
}

/** Where the reset email sends people back to: this CRM's sign-in page. */
export function recoveryRedirectUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/login`;
}
